/**
 * File Handle Store - Uses IndexedDB to persist File System Access API handles
 *
 * This allows the app to remember file locations and automatically reload
 * audio files when opening saved projects (with user permission).
 */

const DB_NAME = 'chop-engine-files';
const DB_VERSION = 1;
const STORE_NAME = 'fileHandles';

let db = null;

// Initialize the IndexedDB database
const initDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('[FileHandleStore] Failed to open IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      console.log('[FileHandleStore] IndexedDB initialized');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Create object store for file handles
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: 'id' });
        console.log('[FileHandleStore] Created fileHandles store');
      }
    };
  });
};

/**
 * Store a file handle with a unique ID
 * @param {string} id - Unique identifier (e.g., layer ID + filename)
 * @param {FileSystemFileHandle} handle - The file handle from File System Access API
 * @param {object} metadata - Additional metadata (filename, size, etc.)
 */
export const storeFileHandle = async (id, handle, metadata = {}) => {
  try {
    const database = await initDB();

    return new Promise((resolve, reject) => {
      const transaction = database.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      const record = {
        id,
        handle,
        metadata: {
          ...metadata,
          storedAt: new Date().toISOString()
        }
      };

      const request = store.put(record);

      request.onsuccess = () => {
        console.log('[FileHandleStore] Stored handle:', id);
        resolve(true);
      };

      request.onerror = () => {
        console.error('[FileHandleStore] Failed to store handle:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('[FileHandleStore] Error storing handle:', error);
    return false;
  }
};

/**
 * Retrieve a file handle by ID
 * @param {string} id - The unique identifier
 * @returns {Promise<{handle: FileSystemFileHandle, metadata: object} | null>}
 */
export const getFileHandle = async (id) => {
  try {
    const database = await initDB();

    return new Promise((resolve, reject) => {
      const transaction = database.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(id);

      request.onsuccess = () => {
        if (request.result) {
          console.log('[FileHandleStore] Retrieved handle:', id);
          resolve(request.result);
        } else {
          resolve(null);
        }
      };

      request.onerror = () => {
        console.error('[FileHandleStore] Failed to get handle:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('[FileHandleStore] Error getting handle:', error);
    return null;
  }
};

/**
 * Get all stored file handles
 * @returns {Promise<Array<{id: string, handle: FileSystemFileHandle, metadata: object}>>}
 */
export const getAllFileHandles = async () => {
  try {
    const database = await initDB();

    return new Promise((resolve, reject) => {
      const transaction = database.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        resolve(request.result || []);
      };

      request.onerror = () => {
        console.error('[FileHandleStore] Failed to get all handles:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('[FileHandleStore] Error getting all handles:', error);
    return [];
  }
};

/**
 * Delete a file handle by ID
 * @param {string} id - The unique identifier
 */
export const deleteFileHandle = async (id) => {
  try {
    const database = await initDB();

    return new Promise((resolve, reject) => {
      const transaction = database.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);

      request.onsuccess = () => {
        console.log('[FileHandleStore] Deleted handle:', id);
        resolve(true);
      };

      request.onerror = () => {
        console.error('[FileHandleStore] Failed to delete handle:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('[FileHandleStore] Error deleting handle:', error);
    return false;
  }
};

/**
 * Request permission to access a file and read it
 * @param {FileSystemFileHandle} handle - The stored file handle
 * @returns {Promise<File | null>} - The File object if permission granted
 */
export const requestFileAccess = async (handle) => {
  try {
    // Check if we already have permission
    const permission = await handle.queryPermission({ mode: 'read' });

    if (permission === 'granted') {
      return await handle.getFile();
    }

    // Request permission if not granted
    const requestResult = await handle.requestPermission({ mode: 'read' });

    if (requestResult === 'granted') {
      return await handle.getFile();
    }

    console.log('[FileHandleStore] Permission denied for file');
    return null;
  } catch (error) {
    console.error('[FileHandleStore] Error requesting file access:', error);
    return null;
  }
};

/**
 * Check if the File System Access API is supported
 * @returns {boolean}
 */
export const isFileSystemAccessSupported = () => {
  return 'showOpenFilePicker' in window;
};

/**
 * Open a file picker and get file handle(s)
 * @param {object} options - Options for the file picker
 * @returns {Promise<FileSystemFileHandle[] | null>}
 */
export const openFilePicker = async (options = {}) => {
  if (!isFileSystemAccessSupported()) {
    console.warn('[FileHandleStore] File System Access API not supported');
    return null;
  }

  try {
    const handles = await window.showOpenFilePicker({
      multiple: options.multiple || false,
      types: options.types || [
        {
          description: 'Audio Files',
          accept: {
            'audio/*': ['.wav', '.mp3', '.m4a', '.ogg', '.flac', '.aiff', '.aif']
          }
        }
      ]
    });

    return handles;
  } catch (error) {
    if (error.name === 'AbortError') {
      // User cancelled the picker
      return null;
    }
    console.error('[FileHandleStore] Error opening file picker:', error);
    return null;
  }
};

/**
 * Create a unique handle ID for a layer
 * @param {number} layerId - The layer ID
 * @param {string} fileName - The file name
 * @returns {string}
 */
export const createHandleId = (layerId, fileName) => {
  return `layer-${layerId}-${fileName}`;
};
